import { isError, isString } from 'lodash'
import type { SerializableValue } from '@axilon-apps/libs-shared-types'

interface Options {
  transport: {
    console?: boolean
    ipc?: boolean
  }
}

const defaultOptions: Options = {
  transport: {
    console: true,
    ipc: true
  }
}

// Fallback used on web where window.api doesn't exist.
// On desktop, the Electron preload script sets window.api via contextBridge,
// which includes a logger that forwards messages to the main process over IPC.
// On web, window.api is undefined, so we fall back to noopLogger. Console
// logging still works — each Logger method has its own console.log/warn/error
// call gated by transport.console. Only the IPC transport is silenced.
const noopLogger = {
  info: (_params: { message: string; params?: SerializableValue }) => {
    /* no-op */
  },
  warn: (_params: { message: string; params?: SerializableValue }) => {
    /* no-op */
  },
  error: (_params: { message: string; params?: unknown }) => {
    /* no-op */
  }
}

class Logger {
  private logger: typeof noopLogger

  constructor(private baseOptions: Options = defaultOptions) {
    // On desktop: window.api.logger exists (set by Electron preload) → use it.
    // On web: window.api is undefined → optional chain falls through to noopLogger.
    // On non-browser runtimes (node-env jest, SSR): no window at all → noopLogger,
    // so merely importing a module that logs never crashes.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const global = (typeof window === 'undefined' ? undefined : window) as unknown as any
    this.logger = global?.api?.logger ?? noopLogger
  }

  public child(options: Partial<Options>) {
    return new Logger(this.options(options))
  }

  public info(message: string, params?: unknown, options?: Partial<Options>) {
    if (this.options(options).transport.console) {
      if (params) {
        console.log(`[LOGGER]: ${message}`, params)
      } else {
        console.log(`[LOGGER]: ${message}`)
      }
    }

    if (this.options(options).transport.ipc && this.logger) {
      this.logger.info({ message, params: params as SerializableValue })
    }
  }

  public warn(message: string, params?: unknown, options?: Partial<Options>) {
    if (this.options(options).transport.console) {
      if (params) {
        console.warn(`[LOGGER]: ${message}`, params)
      } else {
        console.warn(`[LOGGER]: ${message}`)
      }
    }

    if (this.options(options).transport.ipc && this.logger) {
      this.logger.warn({ message, params: params as SerializableValue })
    }
  }

  public error(message: string | Error, params?: unknown, options?: Partial<Options>) {
    if (this.options(options).transport.console) {
      if (params) {
        console.error(`[LOGGER]: ${message}`, params)
      } else {
        console.error(`[LOGGER]: ${message}`)
      }
    }

    if (this.options(options).transport.ipc && this.logger) {
      if (isError(message)) {
        this.logger.error({
          message: message.message,
          params: {
            error: message,
            ...(params || {})
          }
        })
      }

      if (isString(message)) {
        this.logger.error({ message, params: params as SerializableValue })
      }
    }
  }

  private options(params?: Partial<Options>) {
    if (!params) {
      return this.baseOptions
    }

    return {
      ...this.baseOptions,
      ...params
    }
  }
}

export default Logger
